import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ShowMorePopup, { type MinimumItemData, type Props } from './ShowMorePopup';

const ListWithPopup = <TData extends MinimumItemData>({
	items,
	ItemComponent,
	maxLimit,
	initialIsOpen,
	isHoverPopoverEnabled,
	...restProps
}: Props<TData>): React.JSX.Element | null => {
	const [firstItem, ...restItems] = items;

	const ShowMorePopupRenderer = useCallback(
		() => (
			<ShowMorePopup
				items={restItems}
				maxLimit={maxLimit}
				ItemComponent={ItemComponent}
				initialIsOpen={initialIsOpen}
				isHoverPopoverEnabled={isHoverPopoverEnabled}
				{...restProps}
			/>
		),
		[ItemComponent, initialIsOpen, isHoverPopoverEnabled, maxLimit, restItems, restProps],
	);

	if (items.length === 0) {
		return null;
	}

	return (
		<Inline
			space="space.100"
			alignBlock="center"
			shouldWrap={!isHoverPopoverEnabled}
			xcss={isHoverPopoverEnabled && styles.hoverPopover}
		>
			<ItemComponent {...firstItem} isHoverPopoverEnabled={isHoverPopoverEnabled} />

			{restItems.length >= 1 && ShowMorePopupRenderer()}
		</Inline>
	);
};

const styles = cssMap({
	hoverPopover: {
		paddingRight: token('space.050'),
		width: '100%',
	},
});

export default ListWithPopup;
